import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";
import { assertPlatformCheckout } from "./brain-paths.js";

export type FoundryTracingMode = "project" | "account" | "skip";
const FOUNDRY_TRACING_MODES: readonly FoundryTracingMode[] = ["project", "account", "skip"];

/**
 * What an install gets when nobody asks for anything — Foundry GenAI traces go to
 * the install's OWN App Insights, scoped to its own project.
 *
 * This is a CONFIG decision, not a flag, and it is deliberately a named constant
 * rather than three literals. It used to be `skip`, chosen once in argv and then
 * persisted into `system/infra-params` by `enable-auto-update` — so every install
 * was permanently untraced and a one-off `m8t deploy --foundry-tracing project`
 * was silently reverted by the next converge. An untraced agent cannot be
 * diagnosed when a founder says "it did something strange"; the traces are the
 * only record of what it actually saw and said.
 *
 * The cost objection is retired by measurement: at a real install's traffic this
 * is ~64 KB per agent turn — roughly $0.15/month, under the 5 GB/month free grant
 * for any realistic install, and about 2% of the model spend it explains.
 *
 * Three other places must agree with this value: the two installer shell
 * fallbacks, and `param foundryTracingMode` in `deploy/main.bicep` (which decides
 * every path where the value is simply absent). `foundry-tracing-default.test.ts`
 * fails if any of them drift, and sweeps `installer/` — every file type, not just
 * `*.sh` — for a fourth one. It reads all types because a Dockerfile
 * `ENV FOUNDRY_TRACING="skip"` once beat both shell fallbacks silently: an ENV is
 * always non-empty, so `${FOUNDRY_TRACING:-…}` never substituted.
 *
 * `--foundry-tracing skip` remains a working opt-out, on `m8t bootstrap launch`
 * and on `m8t platform enable-auto-update`. TELEMETRY.md documents it, so it has
 * to keep working — but it is not advertised beyond that.
 */
export const DEFAULT_FOUNDRY_TRACING: FoundryTracingMode = "project";

/** Validate the optional --foundry-tracing value; undefined → undefined (bicep default applies). */
export function parseFoundryTracingMode(v: string | undefined): FoundryTracingMode | undefined {
  if (v === undefined) return undefined;
  if (!FOUNDRY_TRACING_MODES.includes(v as FoundryTracingMode)) {
    throw new LocalCliError({
      code: "DEPLOY_INVALID_TRACING_MODE",
      message: `Invalid --foundry-tracing value: '${v}'.`,
      hint: `Use one of: ${FOUNDRY_TRACING_MODES.join(", ")}.`,
    });
  }
  return v as FoundryTracingMode;
}

/** Validate the optional --assign-subscription-roles value; undefined → undefined (bicep default applies). */
export function parseAssignSubscriptionRoles(v: string | undefined): boolean | undefined {
  if (v === undefined) return undefined;
  if (v === "true") return true;
  if (v === "false") return false;
  throw new LocalCliError({
    code: "DEPLOY_INVALID_ASSIGN_SUBSCRIPTION_ROLES",
    message: `Invalid --assign-subscription-roles value: '${v}'.`,
    hint: "Use one of: true, false.",
  });
}

export interface BicepParams {
  location: string;
  suffix: string;
  imageRef: string;
  /** Gateway-only image override. Empty/absent ⇒ the gateway uses `imageRef`.
   *  An infra converge sets this to the gateway's LIVE image: rolling the gateway
   *  is applyGatewayImage's decision (it refuses a BYOC gateway), and `imageRef`
   *  cannot carry it because the voice relay shares that param and does track the
   *  release. */
  gatewayImageRef?: string;
  tenantId: string;
  clientId: string;
  foundryResourceId: string;
  foundryProjectEndpoint: string;
  acrPullIdentityResourceId: string;
  acrResourceId: string;
  foundryTracingMode?: FoundryTracingMode;
  /** When false, main.bicep preserves the existing cross-scope Foundry tracing
   *  connection instead of creating/updating it. Founder deploys omit this and
   *  retain the bicep default true; the unattended updater sets it false. */
  deployFoundryTracingConnection?: boolean;
  provisionUpdater?: boolean;
  installerImage?: string;
  updateCron?: string;
  channelUrl?: string;
  brainCodifyAudience?: string;
  brainCodifyAllowlist?: string;
  brainCodifyWorkflowRef?: string;
  brainCodifyModel?: string;
  brainCodifyEvalModel?: string;
  /** Durable scheduler posture. The Bicep contract uses the exact strings "0"/"1". */
  dreamerEnabled?: string;
  /** Durable Teaching posture. Omitted fields use Bicep defaults only for an
   *  install that has never explicitly recorded Teaching configuration. */
  teachingEnabled?: string;
  teachingModel?: string;
  teachingServiceAppIds?: string;
  teachingRetentionDays?: number;
  /** When false, main.bicep SKIPS its subscription-scoped module(s). The RG-scoped
   *  updater MI cannot deploy at subscription scope, so its converge sets this off;
   *  the founder deploy/CLI (with sub-scope) leaves it default true. */
  assignSubscriptionRoles?: boolean;
  // Referee — all optional; omitted ⇒ the bicep default applies (dormant:
  // gatewayCpu='0.25', gatewayMemory='0.5Gi', every referee/exam var=''). Only
  // set these when explicitly enabling the referee exam stack at deploy time.
  gatewayCpu?: string;
  gatewayMemory?: string;
  refereeEnabled?: string;
  refereeBrainRepos?: string;
  refereeFeedRepo?: string;
  refereeInstallationId?: string;
  refereeWebhookHmacKvUri?: string;
  examKvUri?: string;
  examLaWorkspaceId?: string;
  brainEvalDeployment?: string;
  brainAppLogin?: string;
  refereeCheckpointDir?: string;
  examApiBase?: string;
  /** The live install's voice-relay shared secret, read back from the deployed gateway.
   *  main.bicep defaults this to newGuid(), which re-mints on EVERY deployment and rolls
   *  both the gateway and the relay — so every re-run path must pass the current value.
   *  Omitted/empty ⇒ bicep mints one (correct for a first deploy from zero). */
  voiceInternalSecret?: string;
  /** The gateway's PUBLIC_EZRA_INSTALL_OFFERS and its canary agent, carried
   *  verbatim from the live gateway (readLiveInstallOfferParams) so the CLI
   *  converge and enable-auto-update do not drop them. `m8t deploy` does not
   *  set them. Omitted ⇒ the gateway gets neither variable. */
  publicEzraInstallOffers?: string;
  publicEzraInstallOfferCanaryAgent?: string;
}

export interface BicepOutputs {
  containerAppFqdn: string;
  resourceNames: Partial<Record<string, string>>;
}

/** Build the `key=value` --parameters array for main.bicep (order is stable). */

/**
 * Azure returns a region in two forms and accepts both: the DISPLAY form
 * ("East US 2") from `az containerapp show --query location`, and the canonical
 * form ("eastus2") that deployments actually store. They are the same region and
 * they are not the same string.
 *
 * Normalising here, where every bicep parameter set is assembled, is the only
 * placement that cannot be bypassed: a caller that reads a location off a live
 * resource and passes it through would otherwise set `LOCATION=East US 2` on the
 * updater job while the deployed value reads `eastus2` — a difference that is not
 * a difference, reported on every single what-if run. That is exactly what the
 * infra-drift gate's last standing finding was (2026-08-11), and a converge
 * passing the display form would have churned the job's env on every apply.
 */
export function normalizeAzureLocation(location: string): string {
  return location.trim().toLowerCase().replace(/\s+/g, "");
}

export function buildBicepParams(p: BicepParams): string[] {
  if (p.dreamerEnabled !== undefined && p.dreamerEnabled !== "0" && p.dreamerEnabled !== "1") {
    throw new LocalCliError({
      code: "PLATFORM_DREAMER_ENABLED_INVALID",
      message: `The Dreamer scheduler flag must be '0' or '1', not '${p.dreamerEnabled}'.`,
      hint: "Pass dreamerEnabled=1 to keep Dreamer on, dreamerEnabled=0 to keep it off, or omit it to use the deployment default.",
    });
  }
  if (p.teachingEnabled !== undefined && p.teachingEnabled !== "0" && p.teachingEnabled !== "1") {
    throw new LocalCliError({
      code: "PLATFORM_TEACHING_ENABLED_INVALID",
      message: `The Teaching gate must be '0' or '1', not '${p.teachingEnabled}'.`,
      hint: "Pass teachingEnabled=1 to keep Teaching on, teachingEnabled=0 to keep it off, or omit it to use the deployment default.",
    });
  }
  if (p.teachingModel !== undefined && !p.teachingModel.trim()) {
    throw new LocalCliError({
      code: "PLATFORM_TEACHING_MODEL_REQUIRED",
      message: "The Teaching model must be non-empty when provided.",
      hint: "Pass the existing Foundry deployment name, or omit teachingModel only when the Bicep default is intended.",
    });
  }
  if (
    p.teachingRetentionDays !== undefined &&
    (!Number.isInteger(p.teachingRetentionDays) || p.teachingRetentionDays < 1 || p.teachingRetentionDays > 30)
  ) {
    throw new LocalCliError({
      code: "PLATFORM_TEACHING_RETENTION_INVALID",
      message: `Teaching retention days must be an integer between 1 and 30, not '${String(p.teachingRetentionDays)}'.`,
      hint: "Pass the exact live M8T_TEACHING_RETENTION_DAYS value after verifying it is within 1–30.",
    });
  }
  if (p.brainCodifyModel !== undefined && !p.brainCodifyModel.trim()) {
    throw new LocalCliError({
      code: "PLATFORM_CODIFY_MODEL_REQUIRED",
      message: "The Codify author deployment must be non-empty when provided.",
      hint: "Omit brainCodifyModel to use the deployment default, or pass a Foundry deployment name.",
    });
  }
  if (p.brainCodifyEvalModel !== undefined && !p.brainCodifyEvalModel.trim()) {
    throw new LocalCliError({
      code: "PLATFORM_CODIFY_EVAL_MODEL_REQUIRED",
      message: "The Codify trigger evaluator deployment must be non-empty when provided.",
      hint: "Omit brainCodifyEvalModel to use the deployment default, or pass a Foundry deployment name.",
    });
  }
  if (
    p.brainCodifyModel &&
    p.brainCodifyEvalModel &&
    p.brainCodifyModel === p.brainCodifyEvalModel
  ) {
    throw new LocalCliError({
      code: "PLATFORM_CODIFY_MODELS_MUST_DIFFER",
      message: "The Codify author and trigger evaluator deployments must be different.",
      hint: "Use distinct brainCodifyModel and brainCodifyEvalModel values.",
    });
  }
  const params = [
    `location=${normalizeAzureLocation(p.location)}`,
    `suffix=${p.suffix}`,
    `imageRef=${p.imageRef}`,
    `tenantId=${p.tenantId}`,
    `clientId=${p.clientId}`,
    `foundryResourceId=${p.foundryResourceId}`,
    `foundryProjectEndpoint=${p.foundryProjectEndpoint}`,
    `acrPullIdentityResourceId=${p.acrPullIdentityResourceId}`,
    `acrResourceId=${p.acrResourceId}`,
  ];
  if (p.gatewayImageRef) params.push(`gatewayImageRef=${p.gatewayImageRef}`);
  if (p.foundryTracingMode) params.push(`foundryTracingMode=${p.foundryTracingMode}`);
  if (p.deployFoundryTracingConnection !== undefined) {
    params.push(`deployFoundryTracingConnection=${String(p.deployFoundryTracingConnection)}`);
  }
  if (p.assignSubscriptionRoles !== undefined) params.push(`assignSubscriptionRoles=${String(p.assignSubscriptionRoles)}`);
  if (p.provisionUpdater !== undefined) params.push(`provisionUpdater=${String(p.provisionUpdater)}`);
  if (p.installerImage !== undefined) params.push(`installerImage=${p.installerImage}`);
  if (p.updateCron !== undefined) params.push(`updateCron=${p.updateCron}`);
  if (p.channelUrl !== undefined) params.push(`channelUrl=${p.channelUrl}`);
  if (p.brainCodifyAudience !== undefined) params.push(`brainCodifyAudience=${p.brainCodifyAudience}`);
  if (p.brainCodifyAllowlist !== undefined) params.push(`brainCodifyAllowlist=${p.brainCodifyAllowlist}`);
  if (p.brainCodifyWorkflowRef !== undefined) params.push(`brainCodifyWorkflowRef=${p.brainCodifyWorkflowRef}`);
  if (p.brainCodifyModel !== undefined) params.push(`brainCodifyModel=${p.brainCodifyModel}`);
  if (p.brainCodifyEvalModel !== undefined) params.push(`brainCodifyEvalModel=${p.brainCodifyEvalModel}`);
  if (p.dreamerEnabled !== undefined) params.push(`dreamerEnabled=${p.dreamerEnabled}`);
  if (p.teachingEnabled !== undefined) params.push(`teachingEnabled=${p.teachingEnabled}`);
  if (p.teachingModel !== undefined) params.push(`teachingModel=${p.teachingModel}`);
  if (p.teachingServiceAppIds !== undefined) params.push(`teachingServiceAppIds=${p.teachingServiceAppIds}`);
  if (p.teachingRetentionDays !== undefined) params.push(`teachingRetentionDays=${p.teachingRetentionDays.toString()}`);
  if (p.voiceInternalSecret) params.push(`voiceInternalSecret=${p.voiceInternalSecret}`);
  if (p.publicEzraInstallOffers) params.push(`publicEzraInstallOffers=${p.publicEzraInstallOffers}`);
  if (p.publicEzraInstallOfferCanaryAgent) {
    params.push(`publicEzraInstallOfferCanaryAgent=${p.publicEzraInstallOfferCanaryAgent}`);
  }
  // Referee: only emit params that were explicitly provided. Omitting
  // all of them (the default) reproduces today's exact param set — the CLI-layer
  // dormancy guarantee (see deploy.test.ts).
  if (p.gatewayCpu) params.push(`gatewayCpu=${p.gatewayCpu}`);
  if (p.gatewayMemory) params.push(`gatewayMemory=${p.gatewayMemory}`);
  if (p.refereeEnabled) params.push(`refereeEnabled=${p.refereeEnabled}`);
  if (p.refereeBrainRepos) params.push(`refereeBrainRepos=${p.refereeBrainRepos}`);
  if (p.refereeFeedRepo) params.push(`refereeFeedRepo=${p.refereeFeedRepo}`);
  if (p.refereeInstallationId) params.push(`refereeInstallationId=${p.refereeInstallationId}`);
  if (p.refereeWebhookHmacKvUri) params.push(`refereeWebhookHmacKvUri=${p.refereeWebhookHmacKvUri}`);
  if (p.examKvUri) params.push(`examKvUri=${p.examKvUri}`);
  if (p.examLaWorkspaceId) params.push(`examLaWorkspaceId=${p.examLaWorkspaceId}`);
  if (p.brainEvalDeployment) params.push(`brainEvalDeployment=${p.brainEvalDeployment}`);
  if (p.brainAppLogin) params.push(`brainAppLogin=${p.brainAppLogin}`);
  if (p.refereeCheckpointDir) params.push(`refereeCheckpointDir=${p.refereeCheckpointDir}`);
  if (p.examApiBase) params.push(`examApiBase=${p.examApiBase}`);
  return params;
}

/**
 * Resolve the repo checkout dir from the ~/.m8t/repo-root marker.
 *
 * Both callers read `deploy/` bicep out of the result, so the checkout is checked here:
 * since the install runbook moved to the public agent repository, this marker names THAT
 * clone for anyone who installed from the published paste, and it carries no deploy/.
 */
export async function resolveRepoRoot(home: string = os.homedir()): Promise<string> {
  const marker = path.join(home, ".m8t", "repo-root");
  let root: string;
  try {
    root = (await fs.readFile(marker, "utf8")).trim();
    if (!root) throw new Error("empty");
  } catch {
    throw new LocalCliError({
      code: "DEPLOY_NO_REPO_ROOT",
      message: "~/.m8t/repo-root marker is missing or empty.",
      hint: "Run 'm8t install' from an m8t checkout (it writes the marker), or run 'm8t deploy' from the repo.",
    });
  }
  assertPlatformCheckout(root, "'m8t deploy'", ["deploy"]);
  return root;
}

/** Resolve the Foundry account ARM id from an explicit value or the endpoint. */
export async function resolveFoundryResourceId(
  endpoint: string,
  explicit?: string,
): Promise<string> {
  if (explicit) return explicit;
  const m = /^https:\/\/([^.]+)\.services\.ai\.azure\.com/.exec(endpoint);
  if (!m) {
    throw new LocalCliError({
      code: "DEPLOY_FOUNDRY_ENDPOINT_BAD",
      message: `Cannot parse the Foundry account name from endpoint: ${endpoint}`,
      hint: "Pass --foundry-resource-id <ARM-id> explicitly.",
    });
  }
  const accounts = JSON.parse(
    await runAz([
      "cognitiveservices", "account", "list",
      "--query", `[?name=='${m[1]}'].id`,
      "--output", "json",
    ]),
  ) as string[];
  if (accounts.length === 0) {
    throw new LocalCliError({
      code: "DEPLOY_FOUNDRY_NOT_FOUND",
      message: `No Foundry account named '${m[1]}' in the active subscription.`,
      hint: "Check the endpoint, or pass --foundry-resource-id explicitly.",
    });
  }
  if (accounts.length > 1) {
    throw new LocalCliError({
      code: "DEPLOY_FOUNDRY_AMBIGUOUS",
      message: `Multiple Foundry accounts named '${m[1]}' — ambiguous.`,
      hint: "Pass --foundry-resource-id <ARM-id> to disambiguate.",
    });
  }
  return accounts[0];
}

/**
 * Resolve the ACR ARM resource id for a private-ACR imageRef — needed to scope
 * the AcrPull role assignment when the Bicep provisions the pull identity.
 * Returns '' for public (non-*.azurecr.io) images so the public path is
 * preserved. An explicit --acr-resource-id wins (e.g. a custom login server the
 * host label can't resolve). Throws DEPLOY_ACR_NOT_FOUND when `az acr show`
 * can't find the registry.
 */
export async function resolveAcrResourceId(opts: {
  imageRef: string;
  explicit?: string;
}): Promise<string> {
  if (opts.explicit) return opts.explicit;
  const host = opts.imageRef.split("/")[0];
  if (!host.endsWith(".azurecr.io")) return "";
  const acrName = host.split(".")[0];
  try {
    const id = (await runAz(["acr", "show", "-n", acrName, "--query", "id", "-o", "tsv"])).trim();
    if (!id) throw new Error("empty id");
    return id;
  } catch (e) {
    if (e instanceof LocalCliError) throw e;
    throw new LocalCliError({
      code: "DEPLOY_ACR_NOT_FOUND",
      message: `Cannot resolve the ACR '${acrName}' for image ${opts.imageRef}.`,
      hint: "Create it (az acr create), sign in (az acr login), and push the web image (docker buildx … --push); or pass --acr-resource-id <ARM-id>.",
    });
  }
}

/** Ensure the resource group exists (create with a deployment tag if not). */
export async function ensureResourceGroup(name: string, location: string): Promise<void> {
  const existing = JSON.parse(
    (await runAz(["group", "show", "--name", name, "--output", "json"]).catch(() => "")) || "null",
  ) as Record<string, unknown> | null;
  if (existing) return;
  await runAz(["group", "create", "--name", name, "--location", location, "--tags", "m8t=deployment"]);
}

/**
 * Resolve an explicit AcrPull UA identity override (--acrpull-identity). When
 * empty, returns '' and the Bicep provisions the identity itself (see
 * `provisionAcrPull` in deploy/main.bicep). Pass an external identity id to
 * reference it and skip provisioning (BYO-identity).
 */
export function resolveAcrPullIdentity(opts: { explicit?: string }): string {
  return opts.explicit ?? "";
}

/**
 * Assemble the `m8t deploy --output json` result.
 *
 * Pure, and it lives here beside buildBicepParams rather than inline in the
 * command, because the installer VALUE-asserts `warnings` and an assertion needs
 * something testable to point at. `warnings` is ALWAYS present, even when empty:
 * an absent key and an empty list are different claims, and the installer needs
 * to tell "no advisories" apart from "this build predates the pipe".
 */
export function buildDeployResult(args: {
  webappUrl: string;
  resourceGroup: string;
  clientId: string;
  resourceNames: Record<string, string | undefined>;
  warnings: string[];
}): Record<string, unknown> {
  return {
    webapp: args.webappUrl,
    resourceGroup: args.resourceGroup,
    clientId: args.clientId,
    resources: args.resourceNames,
    warnings: args.warnings,
  };
}

/** Run `az deployment group create` against <repoRoot>/deploy/main.bicep. */
export async function runBicepDeployment(opts: {
  resourceGroup: string;
  repoRoot: string;
  params: string[];
  deploymentName: string;
}): Promise<BicepOutputs> {
  const bicepPath = path.join(opts.repoRoot, "deploy", "main.bicep");
  const result = JSON.parse(
    await runAz([
      "deployment", "group", "create",
      "--name", opts.deploymentName,
      "--resource-group", opts.resourceGroup,
      "--template-file", bicepPath,
      "--parameters", ...opts.params,
      "--output", "json",
    ]),
  ) as { properties?: { outputs?: Partial<Record<string, { value: unknown }>> } };
  const outputs = result.properties?.outputs ?? {};
  const fqdn = outputs.containerAppFqdn?.value;
  if (typeof fqdn !== "string" || !fqdn) {
    throw new LocalCliError({
      code: "DEPLOY_NO_FQDN",
      message: "Bicep deployment did not return containerAppFqdn.",
      hint: "Check deploy/main.bicep outputs and the deployment log.",
    });
  }
  return {
    containerAppFqdn: fqdn,
    resourceNames: (outputs.resourceNames?.value as Record<string, string> | undefined) ?? {},
  };
}
